// @flow

import type {
  Asset,
  AssetsById,
} from 'fc-model-assets/types'

import _reduce from 'lodash/reduce'
import _reverse from 'lodash/reverse'
import _head from 'lodash/head'
import _filter from 'lodash/filter'
import _indexOf from 'lodash/indexOf'
import _map from 'lodash/map'
import _find from 'lodash/find'

// Effect Manager
import { call, fork, put, select, take, race, all } from 'redux-saga/effects'

// Constants
import { FIELD, FARM } from 'fc-model-assets'

// Action Types
import { ADD_ASSET, DELETE_ASSETS, EDIT_ASSETS } from '../constants'

// Selectors
import { assets$, ancestorsIndex$, childrenIndex$ } from 'fc-assets'
import category$ from '../selectors/category'
import datatable from '../datatable'
import assetsModuleOperation$ from './../selectors/operations'
import { selectedSeasonId$ } from 'hfty-seasons'
import { selectedFarmId$ } from 'hfty-assets'
import { jobs$ } from 'hfty-soil-jobs'

// Operator
import assetsOperator from 'hfty-operator-asset'

// Controllers
import assetsCtrl from 'fc-ctrl-assets'
import soilJobCtrl from 'fc-ctrl-sampling-jobs'

// Exports
export default function *watchAssetOperations (): Generator<*, void, *> {
  while (true) {
    const { type } = yield take([ADD_ASSET, DELETE_ASSETS, EDIT_ASSETS])

    switch (type) {
      case ADD_ASSET: yield fork(handleAddAsset); break
      case DELETE_ASSETS: yield fork(handleDeleteAssets); break
      case EDIT_ASSETS: yield fork(handleEditAssets); break
    }
  }
}

export function *handleAddAsset (): Generator<*, void, *> {
  const ancestorsIndex = yield select(ancestorsIndex$)
  let category = yield select(category$)
  const season = yield select(selectedSeasonId$)
  const selectedFarmId = yield select(selectedFarmId$)
  const parentId = _reduce(ancestorsIndex, function (acc: ?number, list: Array<number>): ?number {
    return acc || _head(_reverse([...list])) || null
  }, null)

  if (!category) {
    category = FIELD
  }

  // $FlowFixMe
  const operation = assetsOperator.create({
    model: {
      id: null,
      label: '',
      parent: category === FIELD ? selectedFarmId : parentId || null,
      season: season,
      shape: null,
      category: category,
    },
  })

  yield put(assetsOperator.actionCreators.add(operation))
}

export function *handleEditAssets (): Generator<*, void, *> {
  const assets: AssetsById = yield select(assets$)
  const selectedIds: Array<number> = yield select(datatable.selectors.selectedRecordIds$)

  const operations = selectedIds.map(function (assetId: number): * {
    const asset: Asset = assets[assetId]

    return assetsOperator.create({
      model: asset,
    })
  })

  yield put(assetsOperator.actionCreators.edit(...operations))
}

export function *handleDeleteAssets (): Generator<*, void, *> {
  const jobs = yield select(jobs$)
  const assets: AssetsById = yield select(assets$)
  const selectedIds: Array<number> = yield select(datatable.selectors.selectedRecordIds$)
  const filteredIds = _filter(selectedIds, function (id: number): boolean {
    return _indexOf(selectedIds, assets[id].parent) < 0
  })

  const operations = selectedIds.map(function (assetId: number): * {
    const asset: Asset = assets[assetId]

    return assetsOperator.create({
      model: asset,
    })
  })
  yield put(assetsOperator.actionCreators.delete(...operations))

  const {submit} = yield race({
    submit: take(assetsOperator.actionTypes.submitOperations()),
    cancel: take(assetsOperator.actionTypes.cancelOperations()),
  })

  if (submit) {
    let operations = yield select(assetsModuleOperation$)
    let filteredOperations = _filter(operations, function (op: Object): boolean {
      return _indexOf(filteredIds, op.model.id) >= 0
    })

    yield all(_map(filteredOperations, function (operation: AssetOperation): Object {
      return call(handleDeleteAsset, operation, jobs)
    }))

    yield put(assetsOperator.actionCreators.cancelOperations(operations))
  }
}

export function *handleDeleteAsset (operation: Object, jobs: Array): Generator<*, void, *> {
  const childrenIndex = yield select(childrenIndex$)
  const assetId = operation.model.id
  const {error} = yield call([assetsCtrl, assetsCtrl.delete], {
    id: assetId,
  })

  if (!error) {
    if (operation.model.category === FARM) {
      yield all(_map(childrenIndex[operation.model.id], function (id: number): Object {
        const job = _find(jobs, {field: id})
        if (job) {
          return put(soilJobCtrl.actionCreators.deleted({id: job.id}))
        }
      }))
      yield all(_map(childrenIndex[operation.model.id], function (id: number): Object {
        return put(assetsCtrl.actionCreators.deleted({id: id}))
      }))
    } else if (operation.model.category === FIELD) {
      const job = _find(jobs, {field: operation.model.id})
      if (job) {
        yield put(soilJobCtrl.actionCreators.deleted({id: job.id}))
      }
    }

    yield put(assetsOperator.actionCreators.updateOperation({
      ...operation,
      errors: [],
      submitting: false,
      success: true,
    }))
  } else {
    yield put(assetsOperator.actionCreators.updateOperation({
      ...operation,
      errors: [error],
      submitting: false,
      success: false,
    }))
  }
}
